import { RENDER_DEBUG } from "./debug-flags";

/**
 * Creates a stateful selector that builds a Map from an array with
 * structural sharing. On each call, diffs the new array against the
 * previous Map — unchanged entries reuse the same object reference.
 *
 * This is the core mechanism for reference stability: if only one
 * user's typing_at changed, the Map preserves identity for the other
 * 49 users. Consumers doing Map.get(id) get the same reference →
 * React.memo / useSyncExternalStore skips the re-render.
 *
 * Same algorithm as renderUserMap (shared-queries.tsx), generalized.
 */
export function createIncrementalMapSelector<T, K, V>(
  getKey: (item: T) => K,
  getValue: (item: T) => V,
  isEntryEqual: (prev: V, next: V) => boolean,
): (data: readonly T[]) => Map<K, V> {
  let prevMap = new Map<K, V>();

  return (data: readonly T[]): Map<K, V> => {
    let changed = false;
    const nextMap = new Map<K, V>();

    for (const item of data) {
      const key = getKey(item);
      const nextValue = getValue(item);
      const prevEntry = prevMap.get(key);

      if (prevEntry !== undefined && isEntryEqual(prevEntry, nextValue)) {
        // Reuse previous entry — same reference
        nextMap.set(key, prevEntry);
      } else {
        if (RENDER_DEBUG && prevEntry !== undefined) {
          console.debug("[IncrementalMap] entry CHANGED", key, {
            prev: prevEntry,
            next: nextValue,
          });
        }
        changed = true;
        nextMap.set(key, nextValue);
      }
    }

    // Detect removals
    if (nextMap.size !== prevMap.size) {
      if (RENDER_DEBUG)
        console.debug("[IncrementalMap] size CHANGED", {
          prev: prevMap.size,
          next: nextMap.size,
          dataLen: data.length,
        });
      changed = true;
    } else if (changed) {
      if (RENDER_DEBUG)
        console.debug("[IncrementalMap] entry CHANGED (size same)", {
          size: nextMap.size,
        });
    }

    if (!changed) return prevMap; // Preserve Map identity

    prevMap = nextMap;
    return nextMap;
  };
}

/**
 * Creates a stateful selector that groups items by key into a
 * Map<K, V[]> with per-group structural sharing. If a group's
 * items are all equal to the previous version, the array reference
 * is reused — so map.get(messageId) returns a stable reference
 * unless that message's child data actually changed.
 */
export function createIncrementalGroupSelector<T, K, V>(
  getGroupKey: (item: T) => K,
  getValue: (item: T) => V,
  isEntryEqual: (prev: V, next: V) => boolean,
): (data: readonly T[]) => Map<K, V[]> {
  let prevMap = new Map<K, V[]>();

  return (data: readonly T[]): Map<K, V[]> => {
    // Group incoming data
    const groups = new Map<K, V[]>();
    for (const item of data) {
      const key = getGroupKey(item);
      const val = getValue(item);
      let arr = groups.get(key);
      if (!arr) {
        arr = [];
        groups.set(key, arr);
      }
      arr.push(val);
    }

    // Diff against previous groups
    let changed = false;
    const nextMap = new Map<K, V[]>();

    for (const [key, nextArr] of groups) {
      const prevArr = prevMap.get(key);
      if (
        prevArr &&
        prevArr.length === nextArr.length &&
        nextArr.every((v, i) => isEntryEqual(prevArr[i], v))
      ) {
        nextMap.set(key, prevArr); // Reuse previous array
      } else {
        changed = true;
        nextMap.set(key, nextArr);
      }
    }

    if (nextMap.size !== prevMap.size) changed = true;
    if (!changed) return prevMap;

    prevMap = nextMap;
    return nextMap;
  };
}

/**
 * Creates a stateful selector that builds a Set from an array with
 * identity preservation. Returns the same Set reference if the
 * contents haven't changed.
 */
export function createIncrementalSetSelector<T, K>(
  getKey: (item: T) => K,
): (data: readonly T[]) => Set<K> {
  let prevSet = new Set<K>();
  let prevKeys: string | null = null;

  return (data: readonly T[]): Set<K> => {
    const nextSet = new Set<K>();
    for (const item of data) {
      nextSet.add(getKey(item));
    }

    // Fast identity check: same size + same serialized keys
    const nextKeys = JSON.stringify([...nextSet].sort());
    if (nextKeys === prevKeys) return prevSet;

    prevSet = nextSet;
    prevKeys = nextKeys;
    return nextSet;
  };
}

/**
 * Creates a stateful selector that extracts a single scalar value
 * from an array by key lookup. Returns the previous value if unchanged.
 */
export function createScalarSelector<T, V>(
  find: (data: readonly T[]) => V,
  isEqual: (prev: V, next: V) => boolean = Object.is,
): (data: readonly T[]) => V {
  let prev: V | undefined;
  let initialized = false;

  return (data: readonly T[]): V => {
    const next = find(data);
    if (initialized && isEqual(prev as V, next)) return prev as V;
    prev = next;
    initialized = true;
    return next;
  };
}
